import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { toast } from "sonner";
import { Plug, Save, Users, AlertTriangle, CheckCircle2 } from "lucide-react";

const CONN = ["not_connected", "pending", "connected"];

export default function AdminSellerIntegration() {
  const [ops, setOps] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [connStatus, setConnStatus] = useState("");
  const [edit, setEdit] = useState({});  // email -> {connection_status, native_vendor_id, expiration, notes}

  const load = async () => {
    const [w, m] = await Promise.all([
      adminClient().get("/admin/integrations/native-seller"),
      adminClient().get("/admin/seller-mappings"),
    ]);
    setOps(w.data); setConnStatus(w.data.connection_status); setMappings(m.data.mappings);
  };
  useEffect(() => { load().catch(() => toast.error("Could not load seller operations.")); }, []);

  const saveOps = async () => {
    try {
      await adminClient().put("/admin/integrations/native-seller", {
        connection_status: connStatus, reason: "admin native seller ops config",
      });
      toast.success("Native seller operations configuration saved.");
      load();
    } catch { toast.error("Could not save."); }
  };

  const saveMapping = async (email) => {
    const e = edit[email] || {};
    try {
      await adminClient().post(`/admin/seller-mappings/${email}`, {
        native_vendor_id: e.native_vendor_id ?? undefined, connection_status: e.connection_status ?? undefined,
        expiration: e.expiration ? e.expiration + "T00:00:00Z" : undefined,
        override: e.override ?? undefined, notes: e.notes ?? undefined, reason: "manual mapping review",
      });
      toast.success(`Mapping updated for ${email} — no duplicate account created.`); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update mapping."); }
  };

  const setE = (email, k, v) => setEdit((s) => ({ ...s, [email]: { ...(s[email] || {}), [k]: v } }));

  if (!ops) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="admin-seller-integration">
      <div className="flex items-center gap-2 mb-1"><Plug className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Native Seller Operations</h1></div>
      <p className="text-sm text-muted-foreground mb-5">Read-only mapping between TAVEN identity, membership and each seller's <strong>native TAVEN vendor identity</strong>. Sellers operate entirely inside the TAVEN Native Operating System — there is no external seller portal.</p>

      {/* Operations status */}
      <div className={`border p-4 mb-6 ${ops.connected ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`} data-testid="seller-ops-connection">
        <p className="flex items-center gap-2 font-bold">
          {ops.connected ? <><CheckCircle2 className="w-5 h-5 text-green-600" /> Native Seller Operations Active</> : <><AlertTriangle className="w-5 h-5 text-amber-600" /> Initializing</>}
        </p>
        <p className="text-sm mt-1 text-black/70">{ops.notes}</p>
        <div className="flex items-end gap-3 mt-3">
          <label className="text-xs">Operations status (manual review)
            <select value={connStatus} onChange={(e) => setConnStatus(e.target.value)} data-testid="seller-ops-status-select" className="block mt-0.5 border border-black/15 px-2 py-1.5">{CONN.map((c) => <option key={c} value={c}>{c}</option>)}</select>
          </label>
          <button onClick={saveOps} className="flex items-center gap-1 bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid="seller-ops-save"><Save className="w-3.5 h-3.5" /> Save</button>
        </div>
      </div>

      {/* Seller mappings */}
      <div className="flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-taven-gold" /><h2 className="text-lg font-black font-display">TAVEN Seller ↔ Native Vendor Mappings</h2></div>
      <div className="border border-black/10 bg-white overflow-x-auto" data-testid="seller-mappings">
        <table className="w-full text-sm">
          <thead className="bg-taven-gray/50 text-left"><tr>
            <th className="p-2 font-bold">TAVEN Email</th><th className="p-2 font-bold">Membership</th>
            <th className="p-2 font-bold">Tier / Wholesale</th><th className="p-2 font-bold">Usage/Limit</th>
            <th className="p-2 font-bold">Native Vendor ID</th><th className="p-2 font-bold">Connection</th><th className="p-2 font-bold"></th>
          </tr></thead>
          <tbody>
            {mappings.map((m) => (
              <tr key={m.taven_email} className="border-t border-black/5" data-testid={`mapping-${m.taven_email}`}>
                <td className="p-2 text-xs">{m.taven_email}</td>
                <td className="p-2 text-xs">{m.membership_plan || "—"} <span className="text-muted-foreground">({m.membership_status})</span></td>
                <td className="p-2 text-xs">{m.dropshipping_tier || "—"}{m.wholesale_access ? " · WS" : ""}{m.included_with_pro ? " · Pro" : ""}</td>
                <td className="p-2 text-xs">{m.product_usage}{m.unlimited ? " / ∞" : ` / ${m.product_limit ?? "—"}`}</td>
                <td className="p-2"><input defaultValue={m.native_vendor_id || ""} onChange={(e) => setE(m.taven_email, "native_vendor_id", e.target.value)} placeholder="link…" className="border border-black/15 px-2 py-1 w-28 text-xs" data-testid={`map-vid-${m.taven_email}`} /></td>
                <td className="p-2"><select defaultValue={m.connection_status} onChange={(e) => setE(m.taven_email, "connection_status", e.target.value)} data-testid={`map-conn-${m.taven_email}`} className="border border-black/15 px-2 py-1 text-xs">{CONN.map((c) => <option key={c} value={c}>{c}</option>)}</select></td>
                <td className="p-2"><button onClick={() => saveMapping(m.taven_email)} className="bg-taven-black text-white px-2.5 py-1 text-[11px] font-bold uppercase" data-testid={`map-save-${m.taven_email}`}>Save</button></td>
              </tr>
            ))}
            {mappings.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No members or sellers yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground mt-3">All manual changes are recorded in the membership audit log. Entitlement overrides apply to the member's single identity — never a duplicate account.</p>
    </div>
  );
}
